<template>
    <VueFinalModal
        v-model="computedVisible"
        :class="`aka-${props.type}`"
        class="align-items-center justify-content-center"
        :modal-id="modalId"
        :content-class="contentClass"
        :click-to-close="canBeClosed"
        :esc-to-close="canBeClosed"
        :z-index-fn="() => (type === 'dialog' ? 100001 : 10000)"
        :display-directive="displayDirective"
        content-transition="vfm-fade"
        :aria-labelledby="title && card ? titleId : undefined"
        :aria-label="title && !card ? title : undefined"
    >
        <template v-if="card">
            <Card class="modal-container">
                <div class="d-flex w-100 justify-content-between align-items-center pl-4" style="height: 32px">
                    <div class="dialog-icons p-2 bg-primary">
                        <Icon icon="form" class="text-white" />
                    </div>

                    <div v-if="!blocking">
                        <Tooltip>
                            <Icon @click="onClose" icon="close" class="text-black icon-md mr-2 cursor-pointer" />
                            <template #popper> Close </template>
                        </Tooltip>
                    </div>
                </div>
                <div
                    class="card-body"
                    :class="{
                        'd-flex flex-column align-items-stretch': type === 'form',
                    }"
                    style="overflow: auto"
                >
                    <h5 v-if="title" :id="titleId" class="mb-3">{{ title }}</h5>
                    <slot :on-close="onClose" :disabled="disabled" :on="vOn"></slot>
                </div>
                <div v-if="slots.footer" class="card-footer">
                    <div class="d-flex justify-content-end align-items-center gap-2">
                        <slot name="footer" :on-close="onClose" :disabled="disabled" :on="vOn"> </slot>
                    </div>
                </div>
            </Card>
        </template>
        <slot v-else :on-close="onClose" :disabled="disabled" :on="vOn"></slot>
    </VueFinalModal>
</template>

<script setup lang="ts">
import { uniqueId } from 'lodash';
import { VueFinalModal } from 'vue-final-modal';
import { useAttrs, useSlots } from 'vue';

// #region Component Interface
const props = withDefaults(
    defineProps<{
        static?: boolean;
        modelValue?: any;
        contentClass?: string;
        modalId?: number | string | symbol;
        closeBtnLabel?: string;
        closeBtnHide?: boolean;
        card?: boolean;
        /**
         * A modal the viewer cannot dismiss: no close icon, no click-outside, no Escape. For the
         * rare case where leaving the dialog is not a valid outcome, such as a form that must be
         * completed before the app is usable. Opt-in, so nothing that does not ask for it changes.
         */
        blocking?: boolean;
        type?: 'modal' | 'dialog' | 'form';
        displayDirective?: 'show' | 'visible' | 'if';
        /**
         * The dialog's accessible name. With `card` it is shown as the dialog heading and linked by
         * aria-labelledby; without `card` the slot owns the layout, so it becomes aria-label.
         * A caller that keeps its own heading passes aria-labelledby (or aria-label) instead.
         */
        title?: string;
    }>(),
    {
        modelValue: false,
        contentClass: 'standard-modal',
        closeBtnLabel: 'Close',
        closeBtnHide: false,
        card: true,
        blocking: false,
        type: 'modal',
        displayDirective: 'show',
    },
);
const emits = defineEmits<{
    (e: 'update:modelValue', value: any): void;
}>();
// #endregion
const slots = useSlots();
const attrs = useAttrs();
const titleId = uniqueId('modal-title-');

//modal id
const modalIdLocal = uniqueId('modal-');
const modalId = computed(() => props.modalId ?? modalIdLocal);
provide('aka-modal-info', { modalId });

//visibility value
const modalVisible = ref(false);

const computedVisible = computed({
    get: () => modalVisible.value,
    set: value => {
        modalVisible.value = value;
        if (!value) {
            emits('update:modelValue', typeof props.modelValue === 'boolean' ? false : undefined);
        }
        // emits('update:modelValue', value);
    },
});

watch(toRef(props, 'modelValue'), value => (modalVisible.value = !!value), { immediate: true });

if (import.meta.env.DEV) {
    watch(modalVisible, open => {
        if (open && !props.title && !attrs['aria-label'] && !attrs['aria-labelledby']) {
            console.warn('Modal opened without an accessible name: pass `title`, aria-label or aria-labelledby.');
        }
    });
}

//loading
const childLoading = ref(false);
const onLoadstart = () => (childLoading.value = true);
const onLoadend = () => (childLoading.value = false);

//disabled
const { disabled } = useDisabled({
    combine: computed(() => props.static || childLoading.value),
});

//handle close
const miscStore = useMiscStore();
const canBeClosed = computed(
    () => !props.blocking && !disabled.value /* &&
        !miscStore.formsWantingWarningBeforeUnload.some(v => v.modalId === modalId.value && !v.canBeUnloaded)*/,
);

//events
const onClose = async () => {
    // if (canBeClosed.value) {
    //     computedVisible.value = false;
    // } else {
    //     const modals = miscStore.formsWantingWarningBeforeUnload.filter(
    //         v => v.modalId === modalId.value && !v.canBeUnloaded
    //     );
    //     const results = await Promise.all(modals.map(modal => modal.onCloseAttempt()));
    //     if (!results.some(r => r === false)) {
    //         computedVisible.value = false;
    //     }
    // }
    computedVisible.value = false;
    return 'break';
};

const onCloseForce = () => {
    computedVisible.value = false;
};

//inject close
provide(closeInjectionKey, {
    close: onClose,
    label: () => props.closeBtnLabel,
    show: () => !props.closeBtnHide,
    bind: {
        class: 'btn-outline',
    },
    disabled,
});

const vOn = {
    close: onCloseForce,
    loadstart: onLoadstart,
    loadend: onLoadend,
};

const { on: handleClose } = useDeepEmit('modal:close');
const { on: handleLoadstart } = useDeepEmit('modal:loadstart');
const { on: handleLoadend } = useDeepEmit('modal:loadend');

handleClose(onClose);
handleLoadstart(onLoadstart);
handleLoadend(onLoadend);

defineExpose({
    onClose,
    onLoadstart,
    onLoadend,
});
</script>

<style lang="scss">
$mq-width: 470px;

.dialog-icons {
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    box-shadow: 0 5px 5px rgba(0, 0, 0, 0.25);
}

.dialog-icons svg {
    width: 64px;
    height: 64px;
}

/* MODAL */

.aka-modal .card-body {
    max-height: 65vh;
    overflow-y: auto;
    @media (max-width: $mq-width) {
        max-height: 80vh;
    }
}

.aka-modal,
.aka-form {
    padding: 2.5rem;
    display: flex;
    @media (max-width: $mq-width) {
        padding: 0rem;
    }
}

.aka-modal .vfm--overlay,
.aka-form .vfm--overlay {
    opacity: 70%;
    background-color: black;
}

.aka-modal .vfm__content,
.aka-form .vfm__content {
    display: flex;
    flex-direction: column;
    margin-left: 1rem;
    margin-right: 1rem;
    padding: 1rem;
    width: 90dvw;
    max-width: 742px;
    // overflow-y: auto;

    @media (max-width: $mq-width) {
        margin: 1rem;
        padding: 0;
        width: 100%;
        max-width: 100%;
    }
}

.aka-modal .dialog-icons {
    margin-top: -38px;
}

/* DIALOG */

.aka-dialog {
    padding: 2.5rem;
    display: flex;
}

.aka-dialog .vfm--overlay {
    opacity: 80%;
}

.aka-dialog .vfm__content {
    display: flex;
    flex-direction: column;
    margin-left: 1rem;
    margin-right: 1rem;
    // overflow-y: auto;
    width: 40%;
}

.aka-dialog .dialog-icons {
    margin-top: -38px;
    margin-bottom: 16px;
}

// modifiers

.standard-modal,
.wide-modal {
    // overflow: auto;
    max-height: 100%;

    @media (max-width: $mq-width) {
        max-height: 80dvh;
    }
}

.wide-modal.vfm__content {
    width: 100%;
    max-width: calc(1920px / 2);
}
</style>
